import { Controller } from "@hotwired/stimulus"

// Config-driven column editor for Claims Dashboard.
// Active section: drag-to-reorder + uncheck to move to Available.
// Available section: check to append to the bottom of Active.
// Dispatches "columns:changed" on apply for dirty detection by claims-views controller.
export default class extends Controller {
  static targets = [
    "toggleButton", "dropdown", "columnCount", "switchContainer"
  ]

  static values = {
    config: Array,
    activeColumns: Array,
    turboFrame: String
  }

  connect() {
    this.draftColumns = [...this.activeColumnsValue]
    this.isOpen = false
    this.dragFromIdx = null
    this.dragToIdx = null

    this.handleClickOutside = this.handleClickOutside.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("mousedown", this.handleClickOutside)
    document.addEventListener("keydown", this.handleKeydown)
  }

  disconnect() {
    document.removeEventListener("mousedown", this.handleClickOutside)
    document.removeEventListener("keydown", this.handleKeydown)
  }

  toggleDropdown() {
    if (this.isOpen) {
      this.closeDropdown()
    } else {
      this.openDropdown()
    }
  }

  openDropdown() {
    this.isOpen = true
    this.draftColumns = [...this.activeColumnsValue]
    this.dropdownTarget.classList.remove("hidden")
    this.toggleButtonTarget.classList.add("underline")
    this.renderColumnList()
    this.updateColumnCount()
  }

  closeDropdown() {
    this.isOpen = false
    this.dropdownTarget.classList.add("hidden")
    this.toggleButtonTarget.classList.remove("underline")
  }

  cancel() {
    this.closeDropdown()
  }

  handleClickOutside(event) {
    if (!this.isOpen) return
    if (this.element.contains(event.target)) return
    this.closeDropdown()
  }

  handleKeydown(event) {
    if (!this.isOpen) return
    if (event.key === "Escape") {
      this.closeDropdown()
    }
  }

  // --- Draft toggle ---

  toggleColumn(event) {
    const key = event.currentTarget.dataset.columnKey
    if (!key) return
    const config = this.configValue.find(c => c.key === key)
    if (config && config.required) return

    const idx = this.draftColumns.indexOf(key)
    if (idx >= 0) {
      this.draftColumns.splice(idx, 1)
    } else {
      this.draftColumns.push(key)
    }
    this.enforceRequiredOrder()
    this.renderColumnList()
    this.updateColumnCount()
  }

  // --- Drag and drop (Active section) ---

  dragStart(event) {
    this.dragFromIdx = parseInt(event.params.index, 10)
    event.currentTarget.style.opacity = "0.3"
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move"
      // Safari requires setData to initiate a drag.
      try { event.dataTransfer.setData("text/plain", String(this.dragFromIdx)) } catch (_e) { /* noop */ }
    }
  }

  dragOver(event) {
    event.preventDefault()
    if (event.dataTransfer) event.dataTransfer.dropEffect = "move"
    const idx = parseInt(event.params.index, 10)
    this.clearDropHighlights()
    const row = event.currentTarget
    row.style.background = "var(--core-purple-200, #e0e3ff)"
    row.style.borderColor = "var(--core-purple-300, #c4c8ff)"
    row.style.borderStyle = "dashed"
    this.dragToIdx = idx
  }

  drop(event) {
    event.preventDefault()
    if (this.dragFromIdx === null || this.dragToIdx === null || this.dragFromIdx === this.dragToIdx) {
      this.clearDropHighlights()
      this.dragFromIdx = null
      this.dragToIdx = null
      return
    }
    const [moved] = this.draftColumns.splice(this.dragFromIdx, 1)
    // After the splice above, indices >= dragFromIdx shift down by one, so
    // downward drops need to adjust the target index to land on the hovered row.
    const adjustedTo = this.dragFromIdx < this.dragToIdx ? this.dragToIdx - 1 : this.dragToIdx
    this.draftColumns.splice(adjustedTo, 0, moved)
    this.enforceRequiredOrder()
    this.clearDropHighlights()
    this.dragFromIdx = null
    this.dragToIdx = null
    this.renderColumnList()
    this.updateColumnCount()
  }

  dragEnd(event) {
    event.currentTarget.style.opacity = "1"
    this.clearDropHighlights()
    this.dragFromIdx = null
    this.dragToIdx = null
  }

  clearDropHighlights() {
    const rows = this.switchContainerTarget.querySelectorAll("[data-active-row='true']")
    rows.forEach(row => {
      row.style.background = "transparent"
      row.style.borderColor = "transparent"
      row.style.borderStyle = "solid"
    })
  }

  // Ensure required columns stay at the front in registry order so that e.g.
  // the sticky "Claim ID" column never ends up behind another column.
  enforceRequiredOrder() {
    const required = this.configValue.filter(c => c.required).map(c => c.key)
    const head = required.filter(k => this.draftColumns.includes(k))
    const rest = this.draftColumns.filter(k => !required.includes(k))
    this.draftColumns = [...head, ...rest]
  }

  // --- Apply ---

  apply() {
    this.enforceRequiredOrder()
    const ordered = [...this.draftColumns]

    this.activeColumnsValue = ordered
    this.closeDropdown()
    this.navigateWithColumns()

    this.dispatch("changed", { detail: { columns: ordered } })
  }

  // --- URL serialization & Turbo navigation ---

  navigateWithColumns() {
    const url = new URL(window.location.href)
    const searchParams = url.searchParams

    searchParams.set("columns", this.activeColumnsValue.join(","))
    searchParams.delete("page")

    const frameId = this.turboFrameValue
    if (frameId && document.getElementById(frameId)) {
      // Promote the frame navigation to a Turbo Visit so the URL + history update and the
      // view is restorable on Back. Assigning frame.src directly does NOT consult
      // data-turbo-action in Turbo 8, so we use the public Turbo.visit frame API.
      Turbo.visit(url.toString(), { frame: frameId, action: "replace" })
      return
    }
    window.location.href = url.toString()
  }

  // --- Rendering ---

  renderColumnList() {
    const container = this.switchContainerTarget
    container.innerHTML = ""

    const activeKeys = this.draftColumns
    const activeCols = activeKeys
      .map(k => this.configValue.find(c => c.key === k))
      .filter(Boolean)
    const availableCols = this.configValue
      .filter(c => !activeKeys.includes(c.key))
      .sort((a, b) => a.label.localeCompare(b.label))

    container.appendChild(this.buildActiveSection(activeCols))
    if (availableCols.length > 0) {
      container.appendChild(this.buildAvailableSection(availableCols))
    }
  }

  buildActiveSection(activeCols) {
    const section = document.createElement("div")
    section.className = "px-4 pt-3 pb-1"

    const header = document.createElement("div")
    header.className = "text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2"
    header.textContent = "Active \u00B7 drag to reorder"
    section.appendChild(header)

    activeCols.forEach((col, idx) => {
      section.appendChild(this.buildActiveRow(col, idx))
    })

    return section
  }

  buildActiveRow(col, idx) {
    const row = document.createElement("div")
    row.dataset.activeRow = "true"
    row.style.cssText = [
      "display:flex",
      "align-items:center",
      "gap:8px",
      "padding:7px 8px",
      "border-radius:6px",
      "border:1.5px solid transparent",
      "background:transparent",
      `cursor:${col.required ? "default" : "grab"}`,
      "transition:all 0.1s",
    ].join(";")

    if (!col.required) {
      row.setAttribute("draggable", "true")
      row.dataset.action = "dragstart->claims-columns#dragStart dragover->claims-columns#dragOver drop->claims-columns#drop dragend->claims-columns#dragEnd"
      row.dataset.claimsColumnsIndexParam = String(idx)
    } else {
      // Required rows are not draggable but still a valid drop target (so
      // users can drop above them if they want — we then re-sort required
      // to the front via enforceRequiredOrder).
      row.dataset.action = "dragover->claims-columns#dragOver drop->claims-columns#drop"
      row.dataset.claimsColumnsIndexParam = String(idx)
    }

    row.appendChild(this.buildGripIcon(col.required))
    row.appendChild(this.buildCheckbox(col, true))

    const label = document.createElement("span")
    label.className = "text-[13px] font-medium text-gray-800"
    label.style.flex = "1"
    label.textContent = col.label
    row.appendChild(label)

    if (col.required) {
      const badge = document.createElement("span")
      badge.className = "text-[10px] text-gray-400"
      badge.textContent = "Required"
      row.appendChild(badge)
    }

    return row
  }

  buildAvailableSection(availableCols) {
    const section = document.createElement("div")
    section.className = "px-4 pb-3 pt-2 border-t border-gray-200 mt-1"

    const header = document.createElement("div")
    header.className = "text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2"
    header.textContent = "Available"
    section.appendChild(header)

    availableCols.forEach(col => {
      section.appendChild(this.buildAvailableRow(col))
    })

    return section
  }

  buildAvailableRow(col) {
    const row = document.createElement("div")
    row.className = "flex items-center gap-2 px-2 py-[7px] rounded-md cursor-pointer hover:bg-gray-50"
    row.dataset.columnKey = col.key
    row.dataset.action = "click->claims-columns#toggleColumn"

    // 12px empty spacer to align with the grip column in active rows.
    const spacer = document.createElement("div")
    spacer.style.cssText = "width:12px;flex-shrink:0"
    row.appendChild(spacer)

    row.appendChild(this.buildCheckbox(col, false))

    const label = document.createElement("span")
    label.className = "text-[13px] font-medium text-gray-600"
    label.textContent = col.label
    row.appendChild(label)

    return row
  }

  buildGripIcon(isRequired) {
    const wrapper = document.createElement("div")
    wrapper.style.cssText = `flex-shrink:0;opacity:${isRequired ? 0.15 : 0.35};display:flex;align-items:center;justify-content:center`
    wrapper.innerHTML = `
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <circle cx="4" cy="2.5" r="1" fill="currentColor"/>
        <circle cx="8" cy="2.5" r="1" fill="currentColor"/>
        <circle cx="4" cy="6" r="1" fill="currentColor"/>
        <circle cx="8" cy="6" r="1" fill="currentColor"/>
        <circle cx="4" cy="9.5" r="1" fill="currentColor"/>
        <circle cx="8" cy="9.5" r="1" fill="currentColor"/>
      </svg>
    `
    return wrapper
  }

  buildCheckbox(col, isActive) {
    const box = document.createElement("div")
    box.dataset.columnKey = col.key
    // Only active-row checkboxes need their own click handler. Available rows
    // delegate clicks to the row itself — wiring both would toggle twice.
    if (!col.required && isActive) {
      box.dataset.action = "click->claims-columns#toggleColumn"
    }

    if (isActive) {
      box.className = "rounded-[3px] flex-shrink-0 flex items-center justify-center bg-core-purple-500 border-2 border-core-purple-500"
      box.style.cssText = `width:16px;height:16px;cursor:${col.required ? "not-allowed" : "pointer"};opacity:${col.required ? 0.5 : 1};transition:all 0.15s`
      box.innerHTML = `
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
          <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `
    } else {
      box.className = "rounded-[3px] flex-shrink-0 bg-white border-2 border-gray-300"
      box.style.cssText = "width:16px;height:16px;cursor:pointer;transition:all 0.15s"
    }

    return box
  }

  updateColumnCount() {
    const count = this.draftColumns.length
    this.columnCountTarget.textContent = `${count} column${count !== 1 ? "s" : ""}`
  }
}
